import { Component, inject, signal } from '@angular/core';
import { DialogErrorBar } from '../../core/dialogs/dialog-error-bar/dialog-error-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { email, form, FormField, maxLength, minLength, required } from '@angular/forms/signals';
import { FormFieldWrapper } from '../../core/form/form-field-wrapper/form-field-wrapper';
import { PasswordField } from '../password-field/password-field';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../../core/services/notification-service/notification-service';
import { defer } from 'rxjs';
import { ApiErrorCode } from '../../core/errors/api-error-code';
import { dialogSubmission } from '../../core/dialogs/services/dialog-mutation-service/dialog-submission';
import { ChangeEmailRequest } from '../models/auth.models';

const EMAIL_MIN = 2;
const EMAIL_MAX = 50;

@Component({
  selector: 'app-change-email-dialog',
  imports: [
    DialogErrorBar,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    FormField,
    FormFieldWrapper,
    PasswordField,
  ],
  templateUrl: './change-email-dialog.html',
})
export class ChangeEmailDialog {
  readonly dialogRef = inject(MatDialogRef<ChangeEmailDialog>);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);

  readonly submission = dialogSubmission({ clearErrorOn: () => this.model() });
  /** A failed send is retried by submitting again, so the button says so rather than "Odeslat". */
  readonly canRetry = signal(false);
  readonly currentEmail = this.authService.currentUser()?.email ?? '';

  model = signal<ChangeEmailRequest>({ newEmail: '', currentPassword: '' });

  emailForm = form(this.model, (schemaPath) => {
    required(schemaPath.newEmail, { message: 'E-mail je prázdný' });
    email(schemaPath.newEmail, { message: 'Zadejte platnou e-mailovou adresu' });
    minLength(schemaPath.newEmail, EMAIL_MIN, {
      message: `E-mail musí mít alespoň ${EMAIL_MIN} znaky`,
    });
    maxLength(schemaPath.newEmail, EMAIL_MAX, {
      message: `E-mail může mít maximálně ${EMAIL_MAX} znaků`,
    });
    required(schemaPath.currentPassword, { message: 'Zadejte aktuální heslo' });
  });

  private markAllAsTouched(): void {
    this.emailForm.newEmail().markAsTouched();
    this.emailForm.currentPassword().markAsTouched();
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.markAllAsTouched();

    if (this.emailForm().invalid()) {
      this.notificationService.showValidationError('Formulář není správně vyplněn');
      return;
    }

    this.canRetry.set(false);

    const outcome = await this.submission.run(
      defer(() => this.authService.requestEmailChange(this.model())),
      'ChangeEmailDialog.requestEmailChange',
    );

    if (outcome.outcome === 'success') {
      this.notificationService.showSuccess(
        'Na novou adresu jsme poslali ověřovací odkaz. Původní adresa platí, dokud odkaz neotevřete.',
        8000,
      );
      return;
    }
    // The pending change is stored; only the mail failed. Resending replaces it, so it is safe.
    if (outcome.outcome === 'failed' && outcome.code === ApiErrorCode.EMAIL_SEND_FAILED) {
      this.canRetry.set(true);
    }
  }
}
